import { AsyncSelect, type IAsyncSelectProps } from '@procurify/ui'

import { UserAPI } from '@webapp/api/UserAPI'
import { useProcurifyIntl } from '@webapp/hooks'
import { type User } from '@webapp/models'
import { type PermissionEnum } from '@webapp/platform/iam'
import { getFullName } from '@webapp/utils/user'
import messages from './messages'
import { UserSelectStyled } from './styles/UserSelectStyled'

/**
 * The fields the picker reads from an option. Callers holding a lighter user
 * shape (e.g. the `/users/{id}/basic/` UserSummary) can pass it as the option
 * type instead of a full `User`.
 */
export type UserSelectOption = Pick<User, 'id' | 'firstName' | 'lastName'>

export interface IUserSelectProps<
  Option extends UserSelectOption = User,
  IsMulti extends boolean = false,
> extends IAsyncSelectProps<Option, IsMulti> {
  placeholder?: string
  permissions?: PermissionEnum[]
}

export const UserSelect = <
  Option extends UserSelectOption = User,
  IsMulti extends boolean = false,
>({
  placeholder,
  permissions = [],
  cacheOptions = true,
  ...otherProps
}: IUserSelectProps<Option, IsMulti>) => {
  const intl = useProcurifyIntl()

  const loadOptions = (inputValue, pagination, callback) => {
    const params = {
      ...(inputValue && { search: inputValue }),
      is_active: 'True',
      permission: permissions.join('|'),
      page: pagination.page,
      order_by: 'firstName',
    }

    UserAPI.getBasicList(params)
      .then((res) => {
        callback({
          // Workaround, not a design choice: UserAPI.getBasicList types the
          // SimpleUserProfile rows it serves as User, so three onChange
          // consumers are typed against a User they never fully receive. Type
          // those rows as UserSelectOption and this generic and cast can go.
          options: res.data as unknown as Option[],
          pagination: {
            hasMore: !!res.metadata.pagination.next,
            page: pagination.page + 1,
          },
        })
      })
      .catch(() => null)
  }
  return (
    <UserSelectStyled>
      <AsyncSelect
        cacheOptions={cacheOptions}
        getOptionLabel={(option) => getFullName(option)}
        getOptionValue={(option) => option.id.toString()}
        loadOptions={loadOptions}
        placeholder={
          placeholder || intl.formatMessage(messages.placeholderText)
        }
        {...otherProps}
      />
    </UserSelectStyled>
  )
}

UserSelect.displayName = 'UserSelect'
